import { ref } from 'vue';
import { useProfileStore } from '@/src/store/profile';
import { useGlobalProperties } from '@/src/composables/useGlobalProperties';
import { useKlaviyo } from '@/src/composables/useKlaviyo';
import type { ProfileData } from '@/src/types/profile';
import { PreSessionQuestion } from '@/src/types/profile';
import useDateFormatter from '@/src/composables/useDateFormatter';

export function useProfile() {
  const { getGlobalProperties } = useGlobalProperties();
  const { $api, $toast } = getGlobalProperties();
  const profileStore = useProfileStore();

  const { trackSpecificEvent, trackAdvisorFeeUpdates } = useKlaviyo();
  const trackProfileUpdates = trackSpecificEvent('Profile Updated');

  const isActiveConfirmPopupDeleteAvatar = ref(false);
  const deleteLoadingAvatar = ref(false);

  const getProfile = async () => {
    try {
      const response = await $api?.get('/api/user');
      if (!response?.data) {
        throw new Error('No profile data received');
      }
      profileStore.setProfile(response.data);
      profileStore.setError(null);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      profileStore.setError('Failed to load profile data');
      $toast?.({
        message: 'Failed to load profile data',
        type: 'error',
      });
      throw error;
    } finally {
      profileStore.setLoading(false);
    }
  };

  const getPrivateProfile = async () => {
    try {
      const response = await $api?.get('/api/user/private');
      if (!response?.data) {
        throw new Error('No profile data received');
      }
      profileStore.setProfile(response.data);
      profileStore.setError(null);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      profileStore.setError('Failed to load profile data');
      $toast?.({
        message: 'Failed to load profile data',
        type: 'error',
      });
      throw error;
    } finally {
      profileStore.setLoading(false);
    }
  };

  const getCategories = async () => {
    try {
      const response = await $api?.get('/api/categories');
      profileStore.setCategories(response?.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      $toast?.({
        message: 'Failed to load categories',
        type: 'error',
      });
    }
  };

  const updateProfile = async (updates: Partial<ProfileData>) => {
    const previousLoading = profileStore.loading;
    profileStore.setLoading(true);
    try {
      await $api?.put('/api/user', updates);
      await getProfile();

      const { per_minute_fee_audio, per_minute_fee_video, per_minute_fee_text, ...nonFeeChanges } = updates;

      if (Object.keys(nonFeeChanges).length > 0) {
        trackProfileUpdates(nonFeeChanges);
      }

      await trackAdvisorFeeUpdates({
        per_minute_fee_audio,
        per_minute_fee_video,
        per_minute_fee_text,
      });
    } catch (error) {
      console.error('Failed to update profile:', error);
      $toast?.({
        message: 'Failed to update profile',
        type: 'error',
      });
      throw error;
    } finally {
      profileStore.setLoading(previousLoading);
    }
  };

  const updatePreSessionQuestion = async (preSessionQuestion: PreSessionQuestion) => {
    if (preSessionQuestion.date_of_birth) {
      preSessionQuestion.date_of_birth = useDateFormatter().formatDate(preSessionQuestion.date_of_birth, 'yyyy-MM-dd');
    }
    if (preSessionQuestion.other_person_date_of_birth) {
      preSessionQuestion.other_person_date_of_birth = useDateFormatter().formatDate(
        preSessionQuestion.other_person_date_of_birth,
        'yyyy-MM-dd'
      );
    }
    return $api
      ?.post('/api/user/pre-session-questions', preSessionQuestion)
      .then(async (res: any) => {
        await getProfile();
        return res;
      })
      .catch((error: any) => {
        $toast?.({
          message: 'Failed to update session pre question',
          type: 'error',
        });
        return Promise.reject(error);
      });
  };

  const changePassword = async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    try {
      await $api?.put('/api/user/change-password', {
        current_password: currentPassword,
        password: newPassword,
      });
      $toast?.({
        message: 'Password changed successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to change password:', error);
      $toast?.({
        message: 'Failed to change password',
        type: 'error',
      });
      throw error;
    }
  };

  const updateAvatar = async (formData: FormData) => {
    const previousLoading = profileStore.loading;
    profileStore.setLoading(true);
    try {
      let response;
      // If formData is empty, it means we want to remove the avatar
      if (formData.has('avatar')) {
        response = await $api?.post('/api/user/avatar', formData);
      } else {
        response = await $api?.delete('/api/user/avatar');
      }

      if (!response?.data) {
        throw new Error('No response data');
      }
      await getProfile(); // Refresh profile data to get new avatar
      $toast?.({
        message: formData.has('avatar') ? 'Avatar updated successfully' : 'Avatar removed successfully',
        type: 'success',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update avatar:', error);
      $toast?.({
        message: 'Failed to update avatar',
        type: 'error',
      });
      throw error;
    } finally {
      profileStore.setLoading(previousLoading);
    }
  };

  const deleteAvatar = () => {
    deleteLoadingAvatar.value = true;
    $api
      .delete('/api/user/avatar')
      .then(() => {
        trackProfileUpdates({
          field: {
            name: 'avatar',
            value: null,
          },
        });
        getProfile(); // Refresh profile data
        deleteLoadingAvatar.value = false;
        isActiveConfirmPopupDeleteAvatar.value = false;
      })
      .catch((error: any) => {
        console.error(error.response);
        deleteLoadingAvatar.value = false;
        isActiveConfirmPopupDeleteAvatar.value = false;
      });
  };

  const getPendingUpdates = async () => {
    return $api
      .get('/api/user/pending-updates')
      .then((res: any) => {
        profileStore.setPendingUpdates(res.data.attributes);
        return res.data.attributes;
      })
      .catch((error: any) => {
        console.error('Failed to fetch profile pending updates:', error);
      });
  };

  const deleteAccount = async () => {
    return $api.delete('/api/user').catch((error: any) => {
      console.error(error.response);
    });
  };

  // Password
  const showPasswordModal = ref(false);
  const currentPassword = ref('');
  const newPassword = ref('');
  const confirmPassword = ref('');
  const isChangingPassword = ref(false);

  const pendingCategories = [
    'per_minute_fee_video',
    'per_minute_fee_audio',
    'per_minute_fee_text',
    'name',
    'bio',
    'short_description',
    'skills_and_methods',
    'my_approach',
    'specialities',
    'my_background',
  ];

  const handlePasswordChange = async () => {
    if (newPassword.value !== confirmPassword.value || !currentPassword.value) {
      return;
    }

    isChangingPassword.value = true;
    try {
      await changePassword({
        currentPassword: currentPassword.value,
        newPassword: newPassword.value,
      });
      currentPassword.value = '';
      newPassword.value = '';
      confirmPassword.value = '';
      showPasswordModal.value = false;
    } catch (error) {
      console.error('Failed to change password:', error);
    } finally {
      isChangingPassword.value = false;
    }
  };

  const formatPhoneNumber = (phone: string | number | null): string => {
    const phoneStr = (phone ?? '').toString().replace(/[^\+0-9]/g, '');
    // Considering that phone control adds country phone code for blank phone.
    return phoneStr.length <= 5 ? '' : phoneStr;
  };

  return {
    isActiveConfirmPopupDeleteAvatar,
    deleteLoadingAvatar,
    pendingCategories,

    showPasswordModal,
    currentPassword,
    newPassword,
    confirmPassword,
    isChangingPassword,
    handlePasswordChange,

    getProfile,
    getPrivateProfile,
    getCategories,
    getPendingUpdates,
    updateProfile,
    updatePreSessionQuestion,
    changePassword,
    updateAvatar,
    deleteAvatar,
    formatPhoneNumber,
    deleteAccount,
  };
}
